import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FilterOption } from '@components/abb-table/abb-table.component';
import {
  currencyListQuery,
  regionSettingsMutation,
  timezoneListQuery,
} from '@graphql/general';
import { TranslateService } from '@ngx-translate/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { ToastService } from '@services/ui/toast.service';
import { getErrorMessage } from '@utils/errors';
import { Apollo } from 'apollo-angular';
import {
  CurrencyListQuery,
  CurrencyListQueryVariables,
  RegionSettings,
  TimezoneListQuery,
  TimezoneListQueryVariables,
} from 'gql/graphql';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';

@Component({
  selector: 'abb-region-settings-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    TPipe,
  ],
  template: `
    <h3 class="text-base font-semibold text-abb-font/85">
      {{ 'Time settings' | t }}
    </h3>
    <form id="region-settings-form" (ngSubmit)="onSubmit()">
      <div [formGroup]="form" class="flex flex-col gap-3">
        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Currency' | t }}</mat-label>
          <mat-select
            formControlName="currency"
            id="currency"
            name="currency"
            [panelClass]="'panel'"
          >
            @for (option of currencies$ | async; track option?.value) {
            <mat-option
              [id]="option?.value?.toString() ?? ''"
              [value]="option?.value"
            >
              {{ option.skipTranslation ? option.label : (option.label | t) }}
            </mat-option>
            }
          </mat-select>
        </mat-form-field>
        <div class="w-full flex flex-col">
          <mat-form-field class="w-full" color="primary">
            <mat-label>{{ 'Timezone' | t }}</mat-label>
            <mat-select
              formControlName="timezone"
              id="timezone"
              name="timezone"
              [panelClass]="'panel'"
            >
              @for (option of timezones$ | async; track option?.value) {
              <mat-option
                [id]="option?.value?.toString() ?? ''"
                [value]="option?.value"
              >
                {{ option.skipTranslation ? option.label : (option.label | t) }}
                <small>({{ option?.hint }})</small>
              </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-hint class="text-abb-font/65">
            <small>
              {{ 'Current browser timezone is' | t }}:
              {{ initValues()?.currentBrowserTimezone }}
            </small>
          </mat-hint>
        </div>
      </div>
    </form>
  `,
  styles: ``,
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.general' },
  ],
  host: { class: 'flex flex-col gap-3' },
})
export class RegionSettingsFormComponent implements OnInit, OnDestroy {
  initValues = input<
    | (RegionSettings & {
        currentBrowserTimezone: string;
        webBrowserTime: Date;
      })
    | undefined
  >();
  formValidationStatusChange = output<boolean>();
  formSubmit = output<FormGroup>();
  form = new FormGroup({
    timezone: new FormControl<string>(''),
    currency: new FormControl<string>(''),
  });
  timezones$ = new BehaviorSubject<(FilterOption & { hint: string })[]>([]);
  currencies$ = new BehaviorSubject<FilterOption[]>([]);

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService
  ) {
    this.subscriptions.push(
      this.form.statusChanges.subscribe(() => {
        this.formValidationStatusChange.emit(!this.form.valid);
      }),
      this.apollo
        .watchQuery<TimezoneListQuery, TimezoneListQueryVariables>({
          query: timezoneListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.timezones$.next(
              data.timezoneList.map((t) => ({
                value: t.id,
                label: t.zone,
                skipTranslation: false,
                hint: t.utc_offset,
              }))
            );
          },
          error: (err) => {},
        }),
      this.apollo
        .watchQuery<CurrencyListQuery, CurrencyListQueryVariables>({
          query: currencyListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.currencies$.next(
              data.currencyList.map((c) => ({
                value: c.id,
                label: c.value,
                skipTranslation: false,
              }))
            );
          },
          error: (err) => {},
        })
    );
  }

  ngOnInit(): void {
    this.subscriptions.push(
      combineLatest([
        this.currencies$.asObservable(),
        this.timezones$.asObservable(),
      ]).subscribe(([currencies, timezones]) => {
        this.form.patchValue({
          currency: currencies
            ?.find((c) => c.value === this.initValues()?.currency.id)
            ?.value?.toString(),
          timezone: timezones
            .find((t) => t.value === this.initValues()?.timezone?.id)
            ?.value?.toString(),
        });
      })
    );
  }

  onSubmit(): void {
    this.formSubmit.emit(this.form);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
